import { FiSearch } from "react-icons/fi";
import { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import { showErrorToast } from "../../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { makeRequestAuth } from "../../_config/api";

function Sold() {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const pageSize = 10;
  const maxVisiblePages = 5;

  const [filters, setFilters] = useState({
    keyword: "",
    year: "",
    type: "All",
    month: "All",
    category: "",
  });
  const [propertiesType, setPropertiesType] = useState("All");

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const resData = {
        keyWord: filters.keyword,
        fromDate: "",
        toDate: "",
        status: "sold",
        isDisable: false,
        sortOrder: "desc",
        sortBy: "createdAt",
        pageNo,
        size: pageSize,
        location: "",
        propertyType:
          propertiesType === "All" ? "" : propertiesType.toLowerCase(),
        listingCategory: filters.category && filters.category.toLowerCase(),
        price: "",
        area: "",
      };

      const response = await makeRequestAuth(
        "post",
        "/admin/getPropertyList",
        resData
      );
      setProperties(response.data.list || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching properties:", error);
      showErrorToast("Failed to fetch properties. Please try again later.");
      return;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageNo, filters, propertiesType]);

  const getPageNumbers = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    const start = Math.max(1, pageNo - Math.floor(maxVisiblePages / 2));
    const end = Math.min(start + maxVisiblePages - 1, totalPages);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <>
      <div className="p-4 bg-white shadow rounded-2xl flex flex-col gap-6">
        <ToastContainer />

        {/* Header & Filters */}
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 border-b border-gray-200 pb-3">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              Sold Properties
            </h2>
            <span className="text-sm text-gray-500">Total {totalCount}</span>
          </div>

          <div className="flex flex-wrap w-full lg:w-auto gap-4">
            {/* Search */}
            <div className="relative w-full sm:w-40">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={filters.keyword}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, keyword: e.target.value }));
                  setPageNo(1);
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 bg-gray-100 rounded-xl w-full focus:outline-none focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-2 bg-gray-100 p-2 rounded-xl border border-gray-200 w-full sm:w-auto">
              <label className="text-sm text-gray-700 whitespace-nowrap">
                For:
              </label>
              <select
                value={filters.category}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, category: e.target.value }));
                  setPageNo(1);
                }}
                className="px-4 bg-gray-100 rounded-lg focus:outline-none w-full sm:w-auto"
              >
                <option value="">All</option>
                {
                  ["Rent", "Buy"].map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))
                }
              </select>
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-2 bg-gray-100 p-2 rounded-xl border border-gray-200 w-full sm:w-auto">
              <label className="text-sm text-gray-700 whitespace-nowrap">
                Type:
              </label>
              <select
                value={propertiesType}
                onChange={(e) => {
                  setPropertiesType(e.target.value);
                  setPageNo(1);
                }}
                className="px-4 bg-gray-100 rounded-lg focus:outline-none w-full sm:w-auto"
              >
                <option>All</option>
                {
                  ["apartment", "townhouse", "villa-compound", "land", "building",
                    "villa", "penthouse", "hotel-apartment", "floor", "studio",].map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))
                }
              </select>
            </div>
          </div>
        </div>

        {/* Property List */}
        {isLoading ? (
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#1F2A44]"></div>
          </div>
        ) : (
          properties.map((property) => (
            <div>
              <div
                key={property.id}
                className="flex flex-col md:flex-row justify-between gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200"
              >
                <div className="flex-shrink-0">
                  <img
                    src={property?.uploadedPhotos?.[0] ?? ""}
                    alt="Property"
                    className="w-32 h-32 md:w-36 md:h-36 rounded-lg object-cover"
                  />
                </div>

                <div className="flex flex-col flex-grow gap-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-800 truncate">
                    {property?.uploadBy?.name ?? "-"}
                  </h3>
                  <p className="text-sm text-gray-500 capitalize">
                    Property Type: {property?.propertyType ?? "-"}
                  </p>
                  <p className="text-sm text-gray-500 capitalize">
                    Furnishing: {property?.furnishing ?? "-"}
                  </p>
                  <p
                    className="text-sm text-gray-600 mt-1 line-clamp-3"
                    dangerouslySetInnerHTML={{
                      __html: property?.description ?? "-",
                    }}
                  ></p>
                </div>

                <div className="flex flex-col justify-start md:items-end min-w-[120px] gap-3">
                  <button
                    onClick={() => toggleRow(property._id)}
                    className="text-sm text-blue-500 underline flex items-center gap-1 hover:text-blue-600"
                  >
                    {expandedRows[property._id]
                      ? "Hide Details"
                      : "View Details"}
                    <span>{expandedRows[property._id] ? "▲" : "▼"}</span>
                  </button>
                </div>
              </div>
              {expandedRows[property._id] && (
                <div className="bg-white border rounded-b-xl border-t-0 p-4 mt-[-1px] text-gray-700 text-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    <p>
                      <strong>Email:</strong> {property.uploadBy?.email}
                    </p>
                    <p>
                      <strong>Phone:</strong> {property.uploadBy?.phone}
                    </p>
                    <p className="capitalize">
                      <strong>City:</strong> {property.city}
                    </p>
                    <p>
                      <strong>Local Area:</strong> {property.localityArea}
                    </p>
                    <p>
                      <strong>Price:</strong> {property.price}
                    </p>
                    <p>
                      <strong>Area:</strong> {property.area}
                    </p>
                    <p>
                      <strong>Bathrooms:</strong> {property.bathrooms}
                    </p>
                    <p className="capitalize">
                      <strong>Listing Category:</strong>{" "}
                      {property.listingCategory}
                    </p>
                    <p className="capitalize">
                      <strong>Furnishing:</strong> {property.furnishing}
                    </p>
                    <p className="sm:col-span-2 lg:col-span-3">
                      <strong>Address:</strong> {property.propertyAddress}
                    </p>
                    <div className="sm:col-span-2 lg:col-span-3">
                      <strong>Description:</strong>
                      <p
                        dangerouslySetInnerHTML={{
                          __html: property?.description ?? "-",
                        }}
                      ></p>
                    </div>

                    <p className="capitalize">
                      <strong>Meta Title:</strong>{" "}
                      {property?.matatags?.title ?? "-"}
                    </p>
                    <p className="sm:col-span-2 lg:col-span-3 capitalize">
                      <strong>Meta Description:</strong>{" "}
                      {property?.matatags?.description ?? "-"}
                    </p>

                    <p>
                      <strong>Created At:</strong>{" "}
                      {new Date(property.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {property.uploadedPhotos?.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt={`photo-${i}`}
                        className="w-20 h-20 rounded object-cover border"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}

        <ToastContainer position="top-right" autoClose={3000} />

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-2">
          <div className="text-sm font-bold text-gray-400">
            Showing data{" "}
            <span className="font-medium">{(pageNo - 1) * pageSize + 1}</span>{" "}
            to{" "}
            <span className="font-medium">
              {Math.min(pageNo * pageSize, totalCount)}
            </span>{" "}
            of <span className="font-medium">{totalCount}</span>
          </div>

          <div className="flex items-center space-x-1 text-sm font-bold text-gray-700">
            <button
              className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-400 disabled:opacity-50"
              onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
              disabled={pageNo === 1}
            >
              &laquo;
            </button>

            {getPageNumbers().map((page) => (
              <button
                key={page}
                className={`px-3 py-1 rounded ${page === pageNo
                  ? "bg-orange-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-400"
                  }`}
                onClick={() => setPageNo(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-400 disabled:opacity-50"
              onClick={() =>
                setPageNo((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={pageNo === totalPages}
            >
              &raquo;
            </button>
          </div>
          <div></div>
        </div>
      </div>
    </>
  );
}

export default Sold;
